import { create } from "zustand";
import { collection, onSnapshot, query, where, Unsubscribe } from 'firebase/firestore';
import { PUBLIC_CATALOGUEPATH } from '../constants/paths';

interface PublicTracksState {
  tracks: { [key: string]: any };
  isInitialized: boolean;
  unsubscribe: Unsubscribe | null;

  setTracks: (tracks: { [key: string]: any }) => void;
  queryTracks: (filter?: any) => any[];
  initTracks: (firebase: any) => void;
  clearTracks: () => void;
}

export const usePublicTracksStore = create<PublicTracksState>((set, get) => ({
  tracks: {},
  isInitialized: false,
  unsubscribe: null,

  setTracks: (tracks: { [key: string]: any }) => set({ tracks }),

  queryTracks: () => Object.values(get().tracks),

  /**
   * Initialize tracks listener for the public catalogue
   * Only shows tracks that are public and not hidden by exclusive license
   */
  initTracks: (firebase: any) => {
    const state = get();

    // If already initialized, skip
    if (state.isInitialized) {
      return;
    }

    // Clean up any existing listener
    if (state.unsubscribe) {
      state.unsubscribe();
    }

    // Clear existing tracks
    set({ tracks: {}, isInitialized: false });

    if (!firebase) {
      set({ isInitialized: true });
      return;
    }

    // Query the public catalogue collection
    const publicRef = collection(firebase.db, PUBLIC_CATALOGUEPATH);
    const publicQuery = query(
      publicRef,
      where('isPublic', '==', true),
      where('isHiddenByExclusiveLicense', '==', false)
    );

    const unsubscribe = onSnapshot(publicQuery, (snapshot: any) => {
      const tracks: { [key: string]: any } = {};
      snapshot.forEach((doc: any) => {
        tracks[doc.id] = { ...doc.data(), id: doc.id };
      });
      set({ tracks });
    }, (error: any) => {
      console.error('Error listening to public catalogue:', error);
    });

    set({
      isInitialized: true,
      unsubscribe,
    });
  },

  /**
   * Clear tracks and unsubscribe (for logout)
   */
  clearTracks: () => {
    const state = get();
    if (state.unsubscribe) {
      state.unsubscribe();
    }
    set({
      tracks: {},
      isInitialized: false,
      unsubscribe: null,
    });
  },
}));
